/**
 * composerChips
 *
 * Codex-pattern toolbar chips styled with Forge's existing chat-composer
 * aesthetic — muted-foreground ghost buttons, vertical separators between
 * inline chips, ChevronDownIcon trailing glyph. Matches the visual language
 * of Forge's compact composer chip strip so the /new
 * landing and the in-thread surfaces look like one design family.
 */
import {
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
  type RefObject,
} from "react";
import {
  BotIcon,
  CheckIcon,
  ChevronDownIcon,
  CloudIcon,
  ComputerIcon,
  FolderIcon,
  FolderPlusIcon,
  FolderXIcon,
  GitBranchIcon,
  GitMergeIcon,
  LaptopIcon,
  ListTodoIcon,
  LockOpenIcon,
  SearchIcon,
} from "lucide-react";
import { cn } from "~/lib/utils";
import { ClaudeAI } from "../Icons";
import { NO_ZOOM_FIELD_TEXT_CLASS } from "../../lib/touchTarget";
import { Separator } from "../ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../ui/menu";
import { resolveTypedBranchOption } from "../BranchToolbar.logic";
import { useCopyToClipboard } from "~/hooks/useCopyToClipboard";
import { ProjectSummaryIcon } from "./ProjectSummaryIcon";
import {
  filterBranchesBySearch,
  filterProjectsBySearch,
  INTELLIGENCE_TIERS_IN_ORDER,
  INTELLIGENCE_TIER_LABELS,
  WORKTREE_MODE_LABELS,
  type IntelligenceTier,
  type ProjectSummary,
  type WorktreeMode,
} from "./NewThreadLanding.logic";

// ---------------------------------------------------------------------------
// Shared chip styling
// ---------------------------------------------------------------------------

/**
 * Canonical Forge chip — exact byte-match for the `<Button variant="ghost"
 * size="sm">` + `text-muted-foreground hover:text-foreground` pattern
 * used by ComposerFooterModeControls / ProviderModelPicker / TraitsPicker.
 * Used by all chips below so a row of them reads as one design family with
 * ChatComposer's existing footer.
 *
 * `variant="primary"` bumps to `font-medium text-foreground/80` for chips
 * that should sit a half-step more prominently than the rest (model + runtime
 * mode in Forge's footer use this stronger weight).
 */
export function chipTriggerClass(variant: "default" | "primary" = "default"): string {
  return [
    "inline-flex min-h-touch min-w-touch shrink-0 snap-start touch-manipulation cursor-pointer items-center gap-1.5 whitespace-nowrap",
    "rounded-lg border border-transparent px-3 text-base md:h-7 md:min-h-0 md:min-w-0 md:text-sm",
    variant === "primary"
      ? "font-medium text-foreground/80 hover:text-foreground"
      : "text-muted-foreground hover:text-foreground",
    "hover:bg-accent transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
  ].join(" ");
}

const menuSearchRowClass = "flex h-touch items-center gap-2 px-2 md:h-8";
const menuSearchInputClass = `${NO_ZOOM_FIELD_TEXT_CLASS} h-full min-h-0 flex-1 bg-transparent leading-(--spacing-touch) text-foreground placeholder:text-muted-foreground/70 focus:outline-none md:text-sm md:leading-8`;
type MenuOpenChangeDetails = Parameters<
  NonNullable<ComponentProps<typeof DropdownMenu>["onOpenChange"]>
>[1];

function focusSearchOnKeyboardOpen(
  open: boolean,
  details: MenuOpenChangeDetails,
  inputRef: RefObject<HTMLInputElement | null>,
) {
  if (!open || !(details.event instanceof KeyboardEvent)) return;

  requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
}

/**
 * Inline vertical separator between chips in a composer footer strip.
 * Matches ComposerFooterModeControls' separator exactly (h-4, sm:visible).
 */
export function ChipSeparator() {
  return <Separator orientation="vertical" className="mx-0.5 hidden h-4 sm:block" />;
}

// ---------------------------------------------------------------------------
// Execution chip — runtime/device picker. Project choices are filtered by this.
// ---------------------------------------------------------------------------

export interface ExecutionOption {
  readonly id: string;
  readonly label: string;
  readonly kind: "cloud" | "device";
}

export interface ExecutionChipProps {
  readonly options: readonly ExecutionOption[];
  readonly selectedId: string;
  readonly onSelect: (id: string) => void;
  readonly triggerClassName?: string;
}

export function ExecutionChip({
  options,
  selectedId,
  onSelect,
  triggerClassName,
}: ExecutionChipProps) {
  const selected = options.find((option) => option.id === selectedId) ?? options[0] ?? null;
  const SelectedIcon = selected?.kind === "device" ? ComputerIcon : CloudIcon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(chipTriggerClass("primary"), triggerClassName)}
        aria-label={selected ? `Run on: ${selected.label}` : "Choose runtime"}
      >
        <SelectedIcon className="size-3.5 shrink-0" />
        <span className="max-w-32 truncate md:max-w-24 lg:max-w-[200px]">
          {selected?.label ?? "Run on"}
        </span>
        <ChevronDownIcon className="size-3 shrink-0 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-64 [&_[data-slot=menu-item]]:min-h-touch md:[&_[data-slot=menu-item]]:min-h-7"
      >
        <DropdownMenuGroup>
          {options.map((option) => {
            const OptionIcon = option.kind === "device" ? ComputerIcon : CloudIcon;
            return (
              <DropdownMenuItem
                key={option.id}
                onClick={() => onSelect(option.id)}
                className="grid grid-cols-[1rem_1fr_auto] items-center gap-2"
              >
                <OptionIcon className="size-3.5" />
                <span className="truncate">{option.label}</span>
                {option.id === selectedId ? <CheckIcon className="size-3.5" /> : <span />}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// Project chip — search + list + "Add new" / "Don't work in" actions
// ---------------------------------------------------------------------------

export interface ProjectChipProps {
  readonly projects: readonly ProjectSummary[];
  readonly activeProject: ProjectSummary | null;
  readonly onSelect: (project: ProjectSummary) => void;
  readonly onAddProject: () => void;
  readonly onSelectNoProject: () => void;
  readonly allowNoProject?: boolean;
  /**
   * When true, render as a static label without the dropdown. Used in
   * ChatComposer surfaces where the thread already owns its project.
   */
  readonly readOnly?: boolean;
  readonly triggerClassName?: string;
  /**
   * `inline` renders the trigger as running text rather than a chip: no glyph,
   * no chevron, a dotted underline, and the surrounding font size. Ported from
   * t3code, whose hero headline reads `What should we build in {selector}?`
   * with the project name itself as the control. Same dropdown either way —
   * only the trigger differs, so the picker cannot drift between the two.
   */
  readonly variant?: "chip" | "inline";
}

function isSameProject(a: ProjectSummary | null, b: ProjectSummary): boolean {
  return a !== null && a.id === b.id && a.environmentId === b.environmentId;
}

/** The picker's rows, or the reason there are none. */
function ProjectPickerOptions({
  projects,
  activeProject,
  onSelect,
}: {
  readonly projects: readonly ProjectSummary[];
  readonly activeProject: ProjectSummary | null;
  readonly onSelect: (project: ProjectSummary) => void;
}) {
  if (projects.length === 0) {
    return <div className="px-3 py-2 text-xs text-muted-foreground">No matching projects.</div>;
  }
  return (
    <>
      {projects.map((project) => (
        <DropdownMenuItem
          key={`${project.environmentId}:${project.id}`}
          onClick={() => onSelect(project)}
          className="grid grid-cols-[1rem_1fr_auto] items-center gap-2"
        >
          <ProjectSummaryIcon project={project} className="size-3.5" />
          <span className="truncate">
            {project.name}
            {project.subLabel ? (
              <span className="ml-1.5 text-muted-foreground/70">{project.subLabel}</span>
            ) : null}
          </span>
          {isSameProject(activeProject, project) ? (
            <CheckIcon className="size-3.5 justify-self-end" />
          ) : (
            <span />
          )}
        </DropdownMenuItem>
      ))}
    </>
  );
}

// `inline-block` + `align-baseline`, not `inline`: the underline is a border,
// and a border on an inline box breaks apart across a wrapped line. Baseline
// alignment is what keeps the block sitting on the sentence's own baseline.
// Colours are t3code's resolved-project trigger (`border-foreground/60`), not
// its "Add a project" fallback — the muted fallback tint on a chosen project
// renders the underline almost invisible against the heading.
const inlineTriggerClass =
  "inline-block max-w-64 cursor-pointer truncate border-b border-dotted border-foreground/60 align-baseline text-foreground transition-colors hover:border-foreground/80 focus-visible:rounded-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring";

/**
 * The picker's trigger, in either of its two shapes.
 *
 * Inline it is running text inside the hero sentence; in the strip it is a chip
 * with the project's mark and a chevron. Both shapes were spelled out at the
 * same call site, so the same `isInline` question was asked five times — class,
 * label, test id, content, and (still, correctly) the popup's alignment.
 */
function ProjectChipTrigger({
  inline,
  label,
  hasProject,
  mark,
  triggerClassName,
}: {
  readonly inline: boolean;
  readonly label: string;
  readonly hasProject: boolean;
  readonly mark: ReactNode;
  readonly triggerClassName: string | undefined;
}) {
  const ariaLabel = hasProject ? `Project: ${label}` : "Choose project";

  if (inline) {
    return (
      <DropdownMenuTrigger
        className={cn(inlineTriggerClass, triggerClassName)}
        aria-label={ariaLabel}
        data-testid="project-inline-trigger"
      >
        {label}
      </DropdownMenuTrigger>
    );
  }

  return (
    <DropdownMenuTrigger
      className={cn(chipTriggerClass(), triggerClassName)}
      aria-label={ariaLabel}
    >
      {mark}
      <span className="max-w-32 truncate md:max-w-32 lg:max-w-[200px]">{label}</span>
      <ChevronDownIcon className="size-3 shrink-0 opacity-60" />
    </DropdownMenuTrigger>
  );
}

export function ProjectChip({
  projects,
  activeProject,
  onSelect,
  onAddProject,
  onSelectNoProject,
  allowNoProject = false,
  readOnly,
  triggerClassName,
  variant = "chip",
}: ProjectChipProps) {
  const [query, setQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const filtered = useMemo(() => filterProjectsBySearch(projects, query), [projects, query]);

  const isInline = variant === "inline";
  // Inline, the trigger IS the sentence, so "No project" would read as a
  // statement rather than an invitation — and this is the only picker on a
  // desktop draft now that the strip chip is gone.
  const label = activeProject?.name ?? (isInline ? "Choose a project" : "No project");
  // Dotted underline rather than a button shape: the name is part of a
  // sentence, and a boxed control mid-sentence reads as a form field.
  // With no project chosen there is no identity to draw, so the generic glyph
  // is the honest mark. Once one is chosen the chip shows that project the way
  // the sidebar, the palette and the breadcrumb all show it.
  const activeProjectMark =
    activeProject === null ? (
      <FolderIcon className="size-3.5 shrink-0" />
    ) : (
      <ProjectSummaryIcon project={activeProject} className="size-3.5 shrink-0" />
    );

  if (readOnly) {
    return (
      <span className={cn(chipTriggerClass(), triggerClassName)} aria-label={`Project: ${label}`}>
        {activeProjectMark}
        <span className="max-w-32 truncate md:max-w-32 lg:max-w-[200px]">{label}</span>
      </span>
    );
  }

  return (
    <DropdownMenu
      onOpenChange={(nextOpen, details) => {
        focusSearchOnKeyboardOpen(nextOpen, details, searchInputRef);
      }}
    >
      <ProjectChipTrigger
        inline={isInline}
        label={label}
        hasProject={activeProject !== null}
        mark={activeProjectMark}
        triggerClassName={triggerClassName}
      />
      <DropdownMenuContent
        // Centred under the hero name, t3code's `MenuPopup align="center"`. A
        // start-aligned popup under a centred heading hangs off to one side of
        // the word it belongs to; the chip in the strip is left-aligned in its
        // row, so `start` stays right for that one.
        align={isInline ? "center" : "start"}
        className="w-72 [&_[data-slot=menu-item]]:min-h-touch md:[&_[data-slot=menu-item]]:min-h-7"
      >
        <div className={menuSearchRowClass}>
          <SearchIcon className="size-4 shrink-0 text-muted-foreground md:size-3.5" />
          <input
            ref={searchInputRef}
            type="text"
            aria-label="Search projects"
            placeholder="Search projects"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className={menuSearchInputClass}
          />
        </div>
        <DropdownMenuSeparator />
        <ProjectPickerOptions
          projects={filtered}
          activeProject={activeProject}
          onSelect={onSelect}
        />
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onAddProject}
          className="grid grid-cols-[1rem_1fr_auto] items-center gap-2"
        >
          <FolderPlusIcon className="size-3.5" />
          <span>New project</span>
          <span />
        </DropdownMenuItem>
        {allowNoProject ? (
          <DropdownMenuItem
            onClick={onSelectNoProject}
            className="grid grid-cols-[1rem_1fr_auto] items-center gap-2"
          >
            <FolderXIcon className="size-3.5" />
            <span>Don't use a project</span>
            <span />
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// Worktree chip — "Start in" picker, maps to DraftThreadEnvMode local/worktree
// ---------------------------------------------------------------------------

export interface WorktreeChipProps {
  readonly mode: WorktreeMode;
  readonly onSelectMode: (mode: WorktreeMode) => void;
  readonly triggerClassName?: string;
}

export function WorktreeChip({ mode, onSelectMode, triggerClassName }: WorktreeChipProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(chipTriggerClass(), triggerClassName)}
        aria-label={`Start in: ${WORKTREE_MODE_LABELS[mode]}`}
      >
        <GitMergeIcon className="size-3.5 shrink-0" />
        <span className="max-w-36 truncate md:max-w-28 lg:max-w-[200px]">
          {WORKTREE_MODE_LABELS[mode]}
        </span>
        <ChevronDownIcon className="size-3 shrink-0 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-56 [&_[data-slot=menu-item]]:min-h-touch md:[&_[data-slot=menu-item]]:min-h-7"
      >
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => onSelectMode("local")}>
            <LaptopIcon className="size-3.5" />
            <span className="flex-1">Project root</span>
            {mode === "local" ? <CheckIcon className="size-3.5" /> : null}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onSelectMode("worktree")}>
            <GitMergeIcon className="size-3.5" />
            <span className="flex-1">New worktree</span>
            {mode === "worktree" ? <CheckIcon className="size-3.5" /> : null}
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// Branch chip
// ---------------------------------------------------------------------------

export const DEFAULT_BRANCH_CANDIDATES: readonly string[] = ["main"];

export interface BranchChipProps {
  readonly branch: string;
  readonly knownBranches: readonly string[];
  readonly onSelectBranch: (branch: string) => void;
  readonly triggerClassName?: string;
}

export function BranchChip({
  branch,
  knownBranches,
  onSelectBranch,
  triggerClassName,
}: BranchChipProps) {
  const [query, setQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const filtered = useMemo(
    () => filterBranchesBySearch(knownBranches, query),
    [knownBranches, query],
  );
  // `knownBranches` is only `main` plus the project default, so without this a
  // repository on `develop` filters the list to nothing and the picker is a
  // dead end. Selecting a branch here sets draft state rather than running git,
  // so an unrecognised name is safe to offer.
  const typedBranch = useMemo(
    () => resolveTypedBranchOption({ query, knownBranches }),
    [query, knownBranches],
  );
  // Copy failures are swallowed on purpose: clipboard access is permission
  // gated, and a context-menu item that throws is worse than one that no-ops.
  const { copyToClipboard } = useCopyToClipboard();

  return (
    <DropdownMenu
      onOpenChange={(open, details) => focusSearchOnKeyboardOpen(open, details, searchInputRef)}
    >
      <DropdownMenuTrigger
        className={cn(chipTriggerClass(), triggerClassName)}
        aria-label={`Branch: ${branch}`}
      >
        <GitBranchIcon className="size-3.5 shrink-0" />
        <span className="max-w-24 truncate lg:max-w-[200px]">{branch}</span>
        <ChevronDownIcon className="size-3 shrink-0 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-72 [&_[data-slot=menu-item]]:min-h-touch md:[&_[data-slot=menu-item]]:min-h-7"
      >
        <div className={menuSearchRowClass}>
          <SearchIcon className="size-4 shrink-0 text-muted-foreground md:size-3.5" />
          <input
            ref={searchInputRef}
            type="text"
            aria-label="Search branches"
            placeholder="Search branches"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className={menuSearchInputClass}
          />
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuLabel>Branches</DropdownMenuLabel>
          {filtered.map((candidate) => (
            <DropdownMenuItem
              key={candidate}
              onClick={() => onSelectBranch(candidate)}
              onContextMenu={(event) => {
                event.preventDefault();
                copyToClipboard(candidate, undefined);
              }}
              data-testid={`branch-chip-item-${candidate}`}
              className="grid grid-cols-[1rem_1fr_auto] items-center gap-2"
            >
              <GitBranchIcon className="size-3.5" />
              <span className="truncate">{candidate}</span>
              {candidate === branch ? (
                <CheckIcon className="size-3.5 justify-self-end" />
              ) : (
                <span />
              )}
            </DropdownMenuItem>
          ))}
          {typedBranch ? (
            <DropdownMenuItem
              key={`use-${typedBranch}`}
              onClick={() => onSelectBranch(typedBranch)}
              data-testid="branch-chip-use-typed"
              className="grid grid-cols-[1rem_1fr_auto] items-center gap-2"
            >
              <GitBranchIcon className="size-3.5" />
              <span className="truncate">
                Use <span className="font-medium">{typedBranch}</span>
              </span>
              <span />
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// Permissions chip — muted "Full access" with LockOpenIcon (matches Forge's
// existing runtime-mode chip; NOT the outlined-orange Codex variant)
// ---------------------------------------------------------------------------

export interface PermissionsChipProps {
  readonly label: string;
}

export function PermissionsChip({ label }: PermissionsChipProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={chipTriggerClass()} aria-label={`Permissions: ${label}`}>
        <LockOpenIcon className="size-3 shrink-0" />
        <span>{label}</span>
        <ChevronDownIcon className="size-3 shrink-0 opacity-70" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuGroup>
          <DropdownMenuLabel>Permissions</DropdownMenuLabel>
          <DropdownMenuItem>
            <span className="flex-1">Full access</span>
            <CheckIcon className="size-3.5" />
          </DropdownMenuItem>
          {/* "Read only" permission mode is not yet implemented.
              Kept here as a visual placeholder so the menu indicates the
              option will exist; disabled until the feature lands. */}
          <DropdownMenuItem disabled>
            <span className="flex-1">Read only</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// Thinking chip — "Medium · Normal" reasoning effort + verbosity preset
// ---------------------------------------------------------------------------

export type ThinkingPreset = "low" | "medium" | "high";

const THINKING_LABEL: Record<ThinkingPreset, string> = {
  low: "Low · Concise",
  medium: "Medium · Normal",
  high: "High · Detailed",
};

export interface ThinkingChipProps {
  readonly preset: ThinkingPreset;
  readonly onSelectPreset: (preset: ThinkingPreset) => void;
}

export function ThinkingChip({ preset, onSelectPreset }: ThinkingChipProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={chipTriggerClass()}
        aria-label={`Thinking: ${THINKING_LABEL[preset]}`}
      >
        <span>{THINKING_LABEL[preset]}</span>
        <ChevronDownIcon className="size-3 shrink-0 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        <DropdownMenuGroup>
          <DropdownMenuLabel>Thinking effort</DropdownMenuLabel>
          {(Object.keys(THINKING_LABEL) as ThinkingPreset[]).map((candidate) => (
            <DropdownMenuItem key={candidate} onClick={() => onSelectPreset(candidate)}>
              <span className="flex-1">{THINKING_LABEL[candidate]}</span>
              {candidate === preset ? <CheckIcon className="size-3.5" /> : null}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// Mode chip — Build vs Plan interaction mode (matches Forge's existing
// ComposerFooterModeControls Build/Plan toggle)
// ---------------------------------------------------------------------------

export type InteractionMode = "build" | "plan";

const MODE_LABEL: Record<InteractionMode, string> = {
  build: "Build",
  plan: "Plan",
};

const MODE_DESCRIPTION: Record<InteractionMode, string> = {
  build: "Default mode — agent edits files and runs commands directly.",
  plan: "Plan mode — agent proposes a plan before taking actions.",
};

export interface ModeChipProps {
  readonly mode: InteractionMode;
  readonly onSelectMode: (mode: InteractionMode) => void;
}

export function ModeChip({ mode, onSelectMode }: ModeChipProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={chipTriggerClass()} aria-label={`Mode: ${MODE_LABEL[mode]}`}>
        {mode === "plan" ? (
          <ListTodoIcon className="size-3.5 shrink-0" />
        ) : (
          <BotIcon className="size-3.5 shrink-0" />
        )}
        <span>{MODE_LABEL[mode]}</span>
        <ChevronDownIcon className="size-3 shrink-0 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuGroup>
          <DropdownMenuLabel>Interaction mode</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => onSelectMode("build")}>
            <BotIcon className="size-3.5" />
            <span className="flex-1">
              <span className="block font-medium">Build</span>
              <span className="block text-muted-foreground text-xs">{MODE_DESCRIPTION.build}</span>
            </span>
            {mode === "build" ? <CheckIcon className="size-3.5" /> : null}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onSelectMode("plan")}>
            <ListTodoIcon className="size-3.5" />
            <span className="flex-1">
              <span className="block font-medium">Plan</span>
              <span className="block text-muted-foreground text-xs">{MODE_DESCRIPTION.plan}</span>
            </span>
            {mode === "plan" ? <CheckIcon className="size-3.5" /> : null}
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// Model chip — version + Intelligence tier with nested submenus
// ---------------------------------------------------------------------------

export interface ModelChipProps {
  readonly tier: IntelligenceTier;
  readonly onSelectTier: (tier: IntelligenceTier) => void;
  /** Visible version string, e.g. "4.7" or "Opus 4.7". Defaults to "4.7". */
  readonly modelVersion?: string;
}

export function ModelChip({ tier, onSelectTier, modelVersion = "4.7" }: ModelChipProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={chipTriggerClass("primary")}
        aria-label={`Model: ${modelVersion} ${INTELLIGENCE_TIER_LABELS[tier]}`}
      >
        <ClaudeAI className="size-4 shrink-0 text-muted-foreground/70" />
        <span>{modelVersion}</span>
        <span className="font-normal text-muted-foreground/70">
          {INTELLIGENCE_TIER_LABELS[tier]}
        </span>
        <ChevronDownIcon className="size-3 shrink-0 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuGroup>
          <DropdownMenuLabel>Intelligence</DropdownMenuLabel>
          {INTELLIGENCE_TIERS_IN_ORDER.map((candidate) => (
            <DropdownMenuItem key={candidate} onClick={() => onSelectTier(candidate)}>
              <span className="flex-1">{INTELLIGENCE_TIER_LABELS[candidate]}</span>
              {candidate === tier ? <CheckIcon className="size-3.5" /> : null}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        {/* Fine-grained model and speed sub-menus are intentionally disabled
            until the per-model selection callback lands. */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger disabled>Opus 4.7</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem disabled>Claude Opus 4.7</DropdownMenuItem>
            <DropdownMenuItem disabled>Claude Sonnet 4.6</DropdownMenuItem>
            <DropdownMenuItem disabled>Claude Haiku 4.5</DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger disabled>Speed</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem disabled>Fast</DropdownMenuItem>
            <DropdownMenuItem disabled>Balanced</DropdownMenuItem>
            <DropdownMenuItem disabled>Deep</DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
